import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Divider,
  TextField,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Backup as BackupIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  SettingsBackupRestore as RestoreIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../services/api';

const Backup = () => {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, filename: null });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, filename: null });
  const [createLoading, setCreateLoading] = useState(false);
  const [configForm, setConfigForm] = useState({
    autoBackupEnabled: false,
    autoBackupSchedule: '0 2 * * *',
    maxBackups: 30
  });
  const [configSaving, setConfigSaving] = useState(false);
  const [configEdited, setConfigEdited] = useState(false);

  useEffect(() => {
    loadBackups();
    loadLogs();
    loadConfig();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/backup/list');
      if (response.data.success) {
        setBackups(response.data.data.backups || []);
      } else {
        setError('Failed to load backups');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await api.get('/backup/logs', {
        params: { limit: 50 }
      });
      if (response.data.success) {
        // Backend returns camelCase, normalize to snake_case for consistency
        const logsData = (response.data.data.logs || []).map(log => ({
          id: log.id,
          operation_type: log.operationType || log.operation_type || null,
          backup_type: log.backupType || log.backup_type || null,
          filename: log.filename || null,
          status: log.status || 'unknown',
          file_size: log.fileSize || log.file_size || null,
          created_at: log.createdAt || log.created_at || null,
          error_message: log.errorMessage || log.error_message || null,
          user: log.user || null
        }));
        setLogs(logsData);
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await api.get('/backup/config');
      if (response.data.success) {
        const configData = response.data.data;
        setConfig(configData);
        setConfigForm({
          autoBackupEnabled: configData.autoBackupEnabled || false,
          autoBackupSchedule: configData.autoBackupSchedule || '0 2 * * *',
          maxBackups: configData.maxBackups || 30
        });
        setConfigEdited(false);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [field]: value
    }));
    setConfigEdited(true);
  };

  const handleSaveConfig = async () => {
    try {
      setConfigSaving(true);
      setError(null);
      const response = await api.put('/backup/config', configForm);
      if (response.data.success) {
        setSuccess('Configuration saved successfully. Auto backup scheduler has been restarted.');
        await loadConfig();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.data.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleResetConfig = () => {
    if (config) {
      setConfigForm({
        autoBackupEnabled: config.autoBackupEnabled || false,
        autoBackupSchedule: config.autoBackupSchedule || '0 2 * * *',
        maxBackups: config.maxBackups || 30
      });
      setConfigEdited(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setCreateLoading(true);
      setError(null);
      const response = await api.post('/backup/create');
      if (response.data.success) {
        setSuccess('Backup created successfully');
        await loadBackups();
        await loadLogs();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || 'Failed to create backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create backup');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await api.get(`/backup/download/${filename}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(`Downloaded ${filename}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download backup');
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await api.delete(`/backup/delete/${deleteDialog.filename}`);
      if (response.data.success) {
        setSuccess('Backup deleted successfully');
        await loadBackups();
        await loadLogs();
        setDeleteDialog({ open: false, filename: null });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || 'Failed to delete backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete backup');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      const response = await api.post('/backup/restore', {
        filename: restoreDialog.filename,
      });
      if (response.data.success) {
        setSuccess('Database restored successfully. Please refresh the page.');
        setRestoreDialog({ open: false, filename: null });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to restore backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore backup');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanOld = async () => {
    try {
      setLoading(true);
      const response = await api.post('/backup/clean');
      if (response.data.success) {
        setSuccess(response.data.message || 'Old backups cleaned successfully');
        await loadBackups();
        await loadLogs();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || 'Failed to clean old backups');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clean old backups');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'pending':
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Backup System
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadBackups();
              loadLogs();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<BackupIcon />}
            onClick={handleCreateBackup}
            disabled={createLoading || loading}
          >
            {createLoading ? 'Creating...' : 'Create Backup'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<BackupIcon />} iconPosition="start" label="Backups" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Logs" />
        <Tab icon={<SettingsIcon />} iconPosition="start" label="Configuration" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Backup Files</Typography>
              {config && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleCleanOld}
                  disabled={loading}
                >
                  Clean Old Backups
                </Button>
              )}
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : backups.length === 0 ? (
              <Alert severity="info">No backups found. Create your first backup to get started.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.filename}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {backup.filename}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={backup.type || 'manual'}
                            size="small"
                            color={backup.type === 'auto' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatFileSize(backup.size)}</TableCell>
                        <TableCell>{formatDate(backup.created)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleDownload(backup.filename)}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Restore">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setRestoreDialog({ open: true, filename: backup.filename })}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, filename: backup.filename })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Backup Logs
            </Typography>
            {logs.length === 0 ? (
              <Alert severity="info">No logs found.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Operation</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Filename</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => {
                      const operationType = log.operation_type || log.operationType;
                      const backupType = log.backup_type || log.backupType;
                      const fileSize = log.file_size || log.fileSize;
                      const createdAt = log.created_at || log.createdAt;
                      const filename = log.filename;
                      const status = log.status || 'unknown';
                      
                      return (
                        <TableRow key={log.id}>
                          <TableCell>
                            {operationType ? (
                              <Chip
                                label={operationType}
                                size="small"
                                color={
                                  operationType === 'create'
                                    ? 'primary'
                                    : operationType === 'restore'
                                    ? 'warning'
                                    : operationType === 'delete'
                                    ? 'error'
                                    : operationType === 'download'
                                    ? 'info'
                                    : operationType === 'clean'
                                    ? 'secondary'
                                    : 'default'
                                }
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {backupType ? (
                              <Chip
                                label={backupType}
                                size="small"
                                variant="outlined"
                                color={backupType === 'auto' ? 'primary' : 'default'}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusIcon(status)}
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {status}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {filename ? (
                              <Tooltip title={filename}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {filename}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {fileSize ? formatFileSize(fileSize) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {createdAt ? formatDate(createdAt) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Backup Configuration
                  </Typography>
                  {configEdited && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleResetConfig}
                        disabled={configSaving}
                      >
                        Reset
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveConfig}
                        disabled={configSaving}
                        startIcon={configSaving ? <CircularProgress size={16} /> : null}
                      >
                        {configSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </Stack>
                  )}
                </Box>
                {config ? (
                  <Stack spacing={3}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={configForm.autoBackupEnabled}
                            onChange={(e) => handleConfigChange('autoBackupEnabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              Auto Backup Enabled
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Automatically create backups on a schedule
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                    <Divider />
                    <TextField
                      fullWidth
                      label="Auto Backup Schedule (Cron)"
                      value={configForm.autoBackupSchedule}
                      onChange={(e) => handleConfigChange('autoBackupSchedule', e.target.value)}
                      helperText={
                        <Box>
                          <Typography variant="caption" display="block">
                            Format: minute hour day month weekday
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Examples: '0 2 * * *' (Daily at 2 AM), '0 */6 * * *' (Every 6 hours), '0 0 * * 0' (Weekly on Sunday)
                          </Typography>
                        </Box>
                      }
                      placeholder="0 2 * * *"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SettingsIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Backups to Keep"
                      value={configForm.maxBackups}
                      onChange={(e) => handleConfigChange('maxBackups', parseInt(e.target.value) || 30)}
                      helperText="Old backups will be automatically deleted when this limit is exceeded"
                      inputProps={{
                        min: 1,
                        max: 1000
                      }}
                    />
                    <Divider />
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Backup Directory (Read-only)
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                        {config.backupDir || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Backup files are stored in this directory
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info">Loading configuration...</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Backup Statistics
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Backups
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {backups.length}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Size
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {formatFileSize(
                        backups.reduce((sum, backup) => sum + (backup.size || 0), 0)
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Manual Backups
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {backups.filter((b) => b.type === 'manual' || !b.type).length}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Auto Backups
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">
                      {backups.filter((b) => b.type === 'auto').length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, filename: null })}>
        <DialogTitle>Delete Backup</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.filename}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, filename: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, filename: null })}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Restore Database
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Warning: This is a destructive operation!
            </Typography>
            <Typography variant="body2">
              Restoring from <strong>{restoreDialog.filename}</strong> will replace all current data in the database.
              This action cannot be undone. Make sure you have a current backup before proceeding.
            </Typography>
          </Alert>
          <Typography>
            Are you absolutely sure you want to restore the database from this backup?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, filename: null })}>Cancel</Button>
          <Button onClick={handleRestore} color="warning" variant="contained" disabled={loading}>
            {loading ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Backup;

